import { memo, useContext, useMemo } from 'react';
import { parseZonedDateTime } from '@internationalized/date';
import { Button, IconButton } from '@ds';
import { HandleOpenNewLegModal, LegConnectorConnectionType, RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { useRouteModalActions } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import { useRouteContext } from '@lp/core/contexts/RouteContext';
import { AuthContext } from '@lp/core/auth';
import {
  convertToIanaTimezone,
  formatDateTimeStringToTimeZone,
  pickLocationDisplayName,
  WithExtendedLocation,
} from '@/utils/helpers';
import { LegLocationTypeEnum, RouteLegTypeEnum, RouteLocationType, SegmentTypeEnum } from '@/api/apis/route';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { RouteBuilderPermissions } from '@/permissions';
import { RoutePlanStatus } from '@/api/apis/case';
import { RouteSegmentType } from '@/types/route';
import { LegConnector } from './LegConnector';
import { LocationMarker } from './LocationMarker';

const getLocationString = (location: WithExtendedLocation<RouteLocationType>): string | undefined => {
  if (location.locationType === LegLocationTypeEnum.hub) {
    return location.code || 'HUB';
  }

  const base = pickLocationDisplayName(location);
  if (!base) return '';

  if (location.locationType === LegLocationTypeEnum.hospital) {
    return `DH, ${base}`;
  }

  if (location.locationType === LegLocationTypeEnum.txcenter) {
    return `TXC, ${base}`;
  }

  return base;
};

type RenderLocationMarkerProps = {
  locationType: LegLocationTypeEnum;
  location?: WithExtendedLocation<RouteLocationType>;
  segmentType: SegmentTypeEnum;
  type: 'start' | 'end';
  isAssignEnable: boolean;
  handleHubModalOpen: (hubId: string) => void;
  onHubAssign: () => void;
};

const renderLocationMarker = ({
  locationType,
  location,
  segmentType,
  type,
  handleHubModalOpen,
  onHubAssign,
  isAssignEnable,
}: RenderLocationMarkerProps) => {
  switch (locationType) {
    case LegLocationTypeEnum.hub:
      const hasData = !!location?.id;

      return (
        <>
          <IconButton
            size="sm"
            variant="action"
            className={`w-auto ${hasData ? 'cursor-pointer' : 'cursor-default pointer-events-none'}`}
            disabled={!hasData}
            onPress={() => hasData && handleHubModalOpen(location.id)}
            startContent={
              <LocationMarker variant="filled" type={type} segmentType={segmentType} useMonoFont>
                {hasData ? getLocationString(location) : 'HUB'}
              </LocationMarker>
            }
          />
          {!hasData && isAssignEnable && (
            <Button size="sm" variant="outline" onPress={onHubAssign}>
              Assign
            </Button>
          )}
        </>
      );
    default:
      return location ? (
        <LocationMarker variant="filled" type={type} segmentType={segmentType} useMonoFont>
          {getLocationString(location)}
        </LocationMarker>
      ) : null;
  }
};

type RouteSegmentProps = {
  segment: RouteSegmentType;
  isReadOnly: boolean;
  isDisabled: boolean;
  disableHintText?: string;
  showStartPoint?: boolean;
  showEndPoint?: boolean;
  showTime?: boolean;
  showAddLeg?: boolean;
  prevLegId?: string;
  handleAddNewLeg?: (data: Partial<HandleOpenNewLegModal>) => void;
  handleHubAssign?: (segmentType: SegmentTypeEnum) => void;
  handleRemoveHub?: (segmentType: SegmentTypeEnum) => void;
};

export const EmptyRouteSegment = memo(
  ({
    segment,
    isReadOnly,
    isDisabled,
    disableHintText,
    showStartPoint = false,
    showEndPoint = false,
    showTime = false,
    showAddLeg = false,
    prevLegId,
    handleAddNewLeg,
    handleHubAssign,
    handleRemoveHub,
  }: RouteSegmentProps) => {
    const { checkPermission } = useContext(AuthContext);
    const { routePlanningStatus } = useBaseCaseDetails();
    const { onOpenModal } = useRouteModalActions();
    const { extendedLocationById } = useRouteContext();

    const startLocation = extendedLocationById.get(segment.startLocation.locationId);
    const endLocation = extendedLocationById.get(segment.endLocation.locationId);

    const segmentId = segment.id;
    const isToDonor = segment.type === SegmentTypeEnum.hospital;
    const segmentEndTime = useMemo(() => {
      if (!segment.endTime) return null;

      const parsedDate = parseZonedDateTime(segment.endTime);

      return formatDateTimeStringToTimeZone(
        parsedDate.toDate().toISOString(),
        convertToIanaTimezone(parsedDate.timeZone),
        'MMM dd, HH:mm',
      );
    }, [segment.endTime]);

    const onHubAssign = () => {
      handleHubAssign?.(segment.type);
    };

    const handleHubModalOpen = (hubId?: string) => {
      if (hubId) {
        onOpenModal({
          type: RouteModalTypes.ViewHub,
          context: {
            hubId,
            onRemove: () => handleRemoveHub?.(segment.type),
          },
        });
      }
    };

    const isHubAssignEnabled = useMemo(() => {
      const hasPermission = checkPermission(RouteBuilderPermissions.AssignResource);
      const isPlanningStatus = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;

      return hasPermission && isPlanningStatus && !isReadOnly;
    }, [checkPermission, isReadOnly, routePlanningStatus]);

    return (
      <div className="flex items-stretch gap-4 pr-4">
        <div className="flex flex-col justify-end items-end w-33 translate-y-3 shrink-0">
          {showTime && isToDonor && !!segmentEndTime && (
            <>
              <span className="text-s-r text-base-10">OR:</span>
              <span className="text-s-r text-base-10">{segmentEndTime}</span>
            </>
          )}
        </div>
        <LegConnector
          type={RouteLegTypeEnum.TmdxJetAviaLeg}
          startConnection={LegConnectorConnectionType.startToEnd}
          endConnection={LegConnectorConnectionType.startToEnd}
          segmentType={segment.type}
          showAddLegButton={!isReadOnly && showAddLeg}
          isDisableAddLegButton={isDisabled}
          disableHintText={disableHintText}
          showStartConnector={showStartPoint}
          showBottomConnector={showEndPoint}
          onAddLegClick={() => handleAddNewLeg?.({ segmentId, prevLegId })}
        />
        <div className={`flex flex-col grow ${showStartPoint ? 'gap-16' : 'gap-10'}`}>
          <div className="flex items-center gap-2 -translate-y-2">
            {showStartPoint &&
              renderLocationMarker({
                locationType: segment.startLocation.locationType,
                location: startLocation,
                segmentType: segment.type,
                type: 'start',
                handleHubModalOpen,
                onHubAssign,
                isAssignEnable: isHubAssignEnabled,
              })}
          </div>
          <div className="flex items-center gap-2 translate-y-2">
            {showEndPoint &&
              renderLocationMarker({
                locationType: segment.endLocation.locationType,
                location: endLocation,
                segmentType: segment.type,
                type: 'end',
                handleHubModalOpen,
                onHubAssign,
                isAssignEnable: isHubAssignEnabled,
              })}
          </div>
        </div>
      </div>
    );
  },
);
